<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ $t("damage_item_entry") }}</h4>
        </div>
      </div>
    </div>

    <div class="card table-list-card p-3 bg-white fs-7">
      <form @submit.prevent="submitDamageItem" enctype="multipart/form-data">
        <!-- Header Form Section -->
        <div class="row gy-2 gx-4 mb-3 justify-content-between align-items-center">
          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label small mb-0 me-3" style="width:200px;">{{$t('voucher_no')}}:</label>
            <input type="text" class="form-control" v-model="item.receiving_no" readonly/>
          </div>

          <div class="col-md-6 d-flex align-items-center">
            <label class="form-label small mb-0 me-3" style="width:150px;">{{$t('voucher_date')}}:</label>
            <input type="date" class="form-control" v-model="item.receiving_date" @change="fetchReceivingNo" />
            <div v-if="errors.receiving_date" class="text-danger mt-2">
              <span>{{errors.receiving_date}}</span>
            </div>
          </div>

          <div class="col-md-6 d-flex align-items-start">
            <label class="form-label small mb-0 me-3" style="width:130px;">{{$t('select_item')}}:</label>
            <div class="flex-grow-1 position-relative">
              <!-- Search Input -->
              <input
                type="text"
                v-model="query"
                @input="debouncedSearch"
                :placeholder="$t('search_item_by_name_barcode')"
                class="form-control"
                @keydown="handleKeys"
              />

              <!-- Dropdown Search Results -->
              <div
                v-if="query"
                class="search-results shadow rounded bg-white position-absolute mt-1"
                style="width:100%; max-height: 350px; overflow-y: auto; z-index: 1050; border: 1px solid #e0e0e0;"
                :class="{ 'd-block': results.length > 0, 'd-none': results.length === 0 }"
              >

                <!-- Results List -->
                <ul class="list-group list-group-flush">
                  <li
                    v-for="(item, index) in results"
                    :key="item.id"
                    class="list-group-item d-flex justify-content-between align-items-center"
                    :class="{ 'bg-primary text-white': index === itemIndex }"
                    style="padding: 10px; font-size: 0.95rem; cursor: pointer;"
                  >
                    <!-- Clickable Product Info -->
                    <div
                      class="flex-grow-1 d-flex flex-column"
                      @click="selectItem(item)"
                      style="cursor: pointer;"
                    >
                      <div class="d-flex align-items-center gap-2">
                        <!-- Image Wrapper -->
                        <div
                          style="width:50px; height:50px; border-radius:4px; overflow:hidden; flex-shrink:0;"
                        >
                          <img
                            :src="$getImageUrl(item.images)"
                            alt="Items"
                            style="width:100%; height:100%; object-fit:cover;"
                          />
                        </div>

                        <!-- Item Info -->
                        <div class="d-flex flex-column">
                          <strong class="text-dark">{{ item.item_name }} - {{ item.item_code }}</strong>
                          <span
                            style="font-size: 0.75rem; width: fit-content; color: black"
                          >
                            Stock: {{ item.stock_quantity }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
                <!-- No Results -->
                <div v-if="!results.length" class="text-center text-muted py-3" style="font-size: 0.9rem;">
                  No results found
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="d-flex align-items-center">
              <label
                class="form-label small mb-0 me-3"
                style="width:150px;"
              >
                {{ $t('warehouse_name') }}: <span class="text-danger">*</span>
              </label>

              <VueSelect2
                v-model="item.warehouse_id"
                :options="formattedwarehouses"
                :settings="{
                  placeholder: $t('select_an_option'),
                  allowClear: true,
                  tags: true,
                }"
                class="form-control"
              />
            </div>
            <div class="text-danger mt-1" style="height:1.2em; margin-left: 130px; font-size:0.875rem;">
              <span v-if="errors.warehouse_id">{{ errors.warehouse_id }}</span>
            </div>
          </div>
        </div>

        <!-- item Info Input Section -->
        <div class="row gy-2 gx-4 mb-3 p-3 mx-1 border rounded shadow-sm">
          <!-- Narration -->
          <div class="col-md-6 d-flex align-items-center mb-2">
            <label class="form-label small mb-0 me-3" style="width:100px;">{{$t('narration')}}: <span class="text-danger">*</span></label>
            <div class="flex-grow-1">
              <input
                type="text"
                class="form-control"
                placeholder="Narration"
                v-model="item.narration"
              />
              <div class="text-danger" style="height:1.2em; font-size:0.875rem;">
                <span v-if="errors.narration">{{ errors.narration }}</span>
              </div>
            </div>
          </div>

          <!-- Quantity -->
          <div class="col-md-6 d-flex align-items-center mb-2">
            <label class="form-label small mb-0 me-3" style="width:106px;">
              {{$t('quantity')}}: <span class="text-danger">*</span>
            </label>
            <div class="flex-grow-1">
              <div class="d-flex align-items-center">
                <input
                  type="number"
                  class="form-control me-2"
                  placeholder="Quantity"
                  v-model="item.quantity"
                />
                <button type="button" class="btn btn-info" @click="addItem">
                  {{$t('add')}}
                </button>
              </div>
              <!-- Error aligned under input like narration -->
              <div class="text-danger mt-1" style="font-size:0.875rem; height: 1.2em;">
                <span v-if="errors.quantity">{{ errors.quantity }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="table-responsive mb-3 fixed-table">
          <table class="table table-bordered table-striped align-middle mb-0">
            <thead class="table-light sticky-top">
              <tr>
                <th style="width: 30%">{{$t('item_name')}}</th>
                <th style="width: 30%">{{$t('narration')}}</th>
                <th style="width: 30%">{{$t('quantity')}}</th>
                <th style="width: 10%" >{{$t('action')}}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cust, index) in addedItems" :key="index">
                <td>{{ cust.item_name }}</td>
                <td>{{ cust.narration }}</td>
                <td>{{ cust.quantity }}</td>
                <td>
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeItem(index)"
                  >
                    Delete
                  </button>
                </td>
              </tr>

              <!-- Show empty row if no data -->
              <tr v-if="!addedItems.length">
                <td colspan="4" class="text-center text-muted">No Items added</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="row g-3 align-items-end mt-4">
          <div class="col-auto d-flex align-items-end ms-auto">
            <button type="button" class="btn btn-cancel me-2" @click="cancelForm">{{ $t('cancel') }}</button>
            <button type="submit" class="btn btn-submit" @click="onSubmit">{{ $t('submit') }}</button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiUrls from "@/urls";
import Vue3Datepicker from 'vue3-datepicker';
import VueSelect2 from "vue3-select2-component";
import feather from "../../../../public/assets/js/feather.min";
import { useToast } from 'vue-toastification';
import debounce from 'lodash.debounce'

export default {
  components: {
    Vue3Datepicker,
    VueSelect2,
  },

  setup() {
    const toast = useToast()
    return {toast}
  },

  data() {
    return {
      item: {
        item: null, narration: '', quantity: '', receiving_no: '',
        receiving_date: new Date().toISOString().split('T')[0], stock_quantity: '', warehouse_id: '', item_account_id: ''
      },

      errors: {},
      errorMessage: "",
      query: "",
      results: [],
      itemIndex: 0,
      selectedItem: null,
      addedItems: [],
      warehouses: [],
      debouncedSearch: null,
      warehouse_qty: 0,
    };
  },

  mounted() {
    this.fetchDropdownData();
    this.fetchReceivingNo();
  },

  computed: {
    formattedwarehouses() {
    return (this.warehouses || [])
      .filter(w => w.id !== undefined && w.name)
      .map(w => ({
        id: w.id.toString(),
        text: w.name.toString()
      }));
    }
  },

  methods: {
    async fetchDropdownData() {
      try {
        const [
          receive_noRes, warehouseRes
        ] = await Promise.all([
          this.$axios.get(apiUrls.damage_receive_number.get),
        ]);

        this.item.receiving_no = receive_noRes?.data?.receiving_no || '';

      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    },

    async searchItems() {
      if (!this.query) {
        this.results = []
        this.warehouses = []
        return
      }

      try {
        const response = await this.$axios.get(apiUrls.batch_item_search.get, {
          params: { q: this.query }
        })
        const results = response.data.results || [];
        this.results = results
        this.itemIndex = 0;

        if (
          results.length === 1 &&
          results[0].barcode_qr_code &&
          results[0].barcode_qr_code === this.query
        ) {
          await this.selectItem(results[0])
          return
        }

        // render feather icons
        await this.$nextTick(() => {
          if (window.feather) feather.replace()
        })
      } catch (error) {
        console.error('Search error:', error)
        this.results = []
      }
    },

    selectItem(item) {
      this.selectedItem = item;
      this.query = item.item_name;
      this.item.item = item.id;
      this.item.stock_quantity = item.stock_quantity;
      this.fetchWarehouses();

      this.results = [];
      this.itemIndex = 0;
    },

    handleKeys(event) {
      if (event.key === 'ArrowDown') {
        this.itemIndex = Math.min(this.itemIndex + 1, this.results.length - 1);
        event.preventDefault();
      } else if (event.key === 'ArrowUp') {
        this.itemIndex = Math.max(this.itemIndex - 1, 0);
        event.preventDefault();
      } else if (event.key === 'Enter') {
        const selectedProduct = this.results[this.itemIndex];
        if (selectedProduct) {
          this.selectItem(selectedProduct);

        }
        event.preventDefault();
      }
    },

    addItem() {
      if (!this.selectedItem) {
        this.toast.warning("Please select Item first.");
        return;
      }

      if (this.item.quantity > this.warehouse_qty) {
        this.toast.info("Entered quantity exceeds available warehouse stock.");
        return;
      }

      // Prevent duplicate customer
      const exists = this.addedItems.some(
        c => c.id === this.selectedItem.id
      );

      if (exists) {
        this.toast.info("Item already added.");
        return;
      }

      // Frontend validation before adding to list
      this.errors = {};
      const requiredFields = [
        "item", "narration", "receiving_date", "warehouse_id", "quantity"
      ];
      requiredFields.forEach(field => {
        if (!this.item[field]) this.errors[field] = this.$t('error.required');
      });

      if (Object.keys(this.errors).length > 0) {
        return;
      }

      // item quantity validation.
      const availableQty = Number(this.selectedItem.stock_quantity || 0);
      const inputQty = Number(this.item.quantity || 0);

      if (inputQty <= 0) {
        this.toast.warning(`Invalid quantity for item "${this.selectedItem.item_name}".`);
        return;
      }

      if (inputQty > availableQty) {
        this.toast.warning(`Exceeds ${inputQty} units of "${this.selectedItem.item_name}". Only ${availableQty} available.`);
        return;
      }

      // Store full item data in addedItems
      const newItem = {
        ...this.item,
        id: this.selectedItem.id,
        item_name: this.selectedItem.item_name,
        item_code: this.selectedItem.item_code,
        stock_quantity: this.selectedItem.stock_quantity,
        images: this.selectedItem.images || [],
      };

      this.addedItems.push(newItem);

      // Reset only row fields, not whole form structure
      this.item = {
        item: null, receiving_no: this.item.receiving_no,
        receiving_date: this.item.receiving_date, stock_quantity: '', warehouse_id: '', item_account_id: ''
      };
      // this.resetForm();
      this.errors = {};
      this.query = "";
      this.selectedItem = null;
      this.warehouses = [];

    },

    async fetchWarehouses() {
      if (!this.selectedItem) {
        this.toast.warning("Please select item first.");
        return;
      }

      const itemId = this.selectedItem?.item_account_id;
      if (!itemId) {
        this.toast.warning("Missing item ID.");
        return;
      }

      try {
        const response = await this.$axios.get(`${apiUrls.item_warehouse.get}${itemId}/`);
        this.warehouses = response.data.results;

        // render feather icons
        await this.$nextTick(() => {
          if (window.feather) feather.replace()
        })
      } catch (error) {
        console.error('Search error:', error)
        this.warehouses = [];
      }
    },

    async onChange(warehouseId) {
      if (!this.selectedItem) {
        this.toast.warning("Please select item first.");
        return;
      }

      try {
        const response = await this.$axios.get(apiUrls.item_warehouse_qty.get, {
          params: {
            item_id: this.selectedItem.item_account_id,
            warehouse_id: warehouseId
          }
        });

        this.warehouse_qty = response.data.total_qty || 0;

      } catch (error) {
        console.error(error);
        this.item.stock_quantity = 0;
      }
    },

    async fetchReceivingNo() {
      try {
        let res = await this.$axios.get(apiUrls.damage_receive_number.get, {
          params: { receiving_date: this.item.receiving_date }
        });
        this.item.receiving_no = res.data.receiving_no;
      } catch (err) {
        console.error(err);
      }
    },

    async submitDamageItem() {
      if (!this.addedItems.length) {
        this.toast.warning("Please add at least one Item before submitting.");
        return;
      }

      try {
        // if your API expects JSON
        const response = await this.$axios.post(apiUrls.damage_item.post, this.addedItems );

        await this.fetchReceivingNo();
        const updatedReceivingNo = this.item.receiving_no;
        this.toast.success("successfully submitted!");
        this.$emit("create_success");

        // Reset form after save
        this.item = {
          item: null, narration: '', quantity: '',
          receiving_no: updatedReceivingNo, receiving_date: this.item.receiving_date, stock_quantity: '',
          warehouse_id: '', item_account_id: ''
        };
        this.selectedItem = null;
        this.addedItems = [];

      } catch (error) {
        if (error.response?.data?.error) {
          this.errorMessage = error.response.data.error;
        }
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        }
      }
    },

    // cancelForm() {
    //   this.$router.push('/batches');
    // },

    removeItem(index) {
      this.addedItems.splice(index, 1);
    },

    resetForm() {
      // Reset form fields and errors
      this.item = {
        item: '', narration: '',
        receiving_no: '', receiving_date: '', stock_quantity: '', warehouse_id: '', item_account_id: ''
      };
      this.errors = {};
      this.errorMessage = "";
    }
  },

  watch: {
    'item.receiving_date'(newDate) {
      if (newDate) {
        this.fetchReceivingNo();
      } else {
        this.item.receiving_no = '';
      }
    },

    'item.warehouse_id'(newVal) {
      if (newVal) {
        this.onChange(newVal);
      }
    }
  },

  created() {
    this.debouncedSearch = debounce(this.searchItems, 300)
  },

};
</script>


<style scoped>
/* Optional: adjust table and input heights */
.table td, .table th {
  vertical-align: middle;
}

.fixed-table {
  min-height: 350px;
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 5px;
}

</style>

